import type {ReactElement} from 'react';

import type {Participant, ParticipantID, RoomID} from '@pexip/infinity';
import type {DeniedDevices} from '@pexip/media';
import type {MediaDeviceInfoLike} from '@pexip/media-control';
import type {
    InboundAudioMetrics,
    InboundVideoMetrics,
    OutboundAudioMetrics,
    OutboundVideoMetrics,
} from '@pexip/peer-connection-stats';

import type {PanelAnimationTypes} from './utils/meetingPanels';

export type {ParticipantID, RoomID, Participant} from '@pexip/infinity';

export type DeviceError = {
    title: string;
    description?: string | ReactElement;
    deniedDevice?: DeniedDevices;
};

export type DeviceErrors = {videoError: DeviceError; audioError: DeviceError};

export interface DeviceStatusInfo {
    controls?: 'media' | 'permissions' | 'permissions-dismiss';
    distinctiveTitles?: DeviceErrors;
    error?: DeniedDevices;
    retriable?: boolean;
    message: string | ReactElement;
    title: string;
    warning?: DeniedDevices;
}

export type AudioMeterState = {level: number; peak: number; enabled?: boolean};

export enum PreflightJoinDetailsToRender {
    BlockedDeviceSelection = 'blocked-device-selection',
    DeviceSelection = 'device-selection',
    Spinner = 'spinner',
}

export enum BlockedBrowserPermissionsInfoType {
    Void = 'void',
    CompleteReload = 'complete-reload',
    Complete = 'complete',
}

export type MeetingPanelType =
    | 'participants'
    | 'chat'
    | 'add'
    | 'breakoutRooms'
    | 'plugins'
    | 'transcripts'
    | 'meetingManagerRaisedHand'
    | 'meetingManagerWaitingInLobby'
    | 'meetingManagerPlugins'
    | 'meetingManagerBreakoutRooms'
    | 'meetingManagerBreakoutRoomsAskingForHelp';

export type MeetingPanelsState = {
    openBreakoutRoomsPanel: boolean;
    openChatPanel: boolean;
    openParticipantPanel: boolean;
    openPluginsPanel: boolean;
    openAddParticipant: boolean;
    openTranscriptsPanel: boolean;
    openMeetingManagerRaisedHand: boolean;
    openMeetingManagerWaitingInLobby: boolean;
    openMeetingManagerPlugins: boolean;
    openMeetingManagerBreakoutRooms: boolean;
    openMeetingManagerBreakoutRoomsAskingForHelp: boolean;
    animationType: PanelAnimationTypes;
};

export type MeetingPanelActions = {
    togglePanels: (panel: MeetingPanelType) => void;
};

export enum PresentationType {
    Images = 'images',
    Video = 'video',
}

export enum PresentationEmphasis {
    Primary = 'primary',
    Secondary = 'secondary',
}

export enum PresentationSize {
    Large = 'large',
    Full = 'full',
}

export enum PresentationAction {
    Start = 'start',
    Reset = 'reset',
    Receive = 'receive',
    SetLocalMediaStream = 'setLocalMediaStream',
    SetRemoteMediaStream = 'setRemoteMediaStream',
    SetEmphasis = 'setEmphasis',
    SetExpandPrimary = 'setExpandPrimary',
    SetShowSteal = 'setShowSteal',
    SetSize = 'setSize',
    SetPresenterName = 'setPresenterName',
    ActivityChange = 'ActivityChange',
}

export type PresentationEvent =
    | {type: PresentationAction.SetLocalMediaStream; mediaStream?: MediaStream}
    | {type: PresentationAction.SetRemoteMediaStream; mediaStream?: MediaStream}
    | {type: PresentationAction.SetEmphasis; emphasis: PresentationEmphasis}
    | {type: PresentationAction.SetExpandPrimary; expandPrimary: boolean}
    | {type: PresentationAction.SetShowSteal; showSteal: boolean}
    | {type: PresentationAction.SetSize; size: PresentationSize}
    | {
          type: PresentationAction.SetPresenterName;
          presenterName?: string;
      }
    | {
          type: PresentationAction.ActivityChange;
          activity: PresentationActivityState;
      };

export interface PresentationActivityState {
    send: boolean;
    recv: boolean;
}

export interface PresentationState {
    emphasis?: PresentationEmphasis;
    expandPrimary?: boolean;
    showSteal?: boolean;
    size?: PresentationSize;
    localMediaStream?: MediaStream;
    remoteMediaStream?: MediaStream;
    activity?: PresentationActivityState;
    presenterName?: string;
}

export enum StreamStatus {
    /* BrowserPip - stream is currently within the browser picture-in-picture window */
    BrowserPip = 'BrowserPip',

    /* Emphasized - stream fills as much of the stage as possible */
    Emphasized = 'Emphasized',

    /* External - equivalent to being popped out in a separate window */
    /* note: currently only used by the presentation stream */
    /* note: perhaps utilized in the future by the meeting mix stream */
    External = 'External',

    /* Deemphasized - stream fills any leftover space on the stage */
    Deemphasized = 'Deemphasized',

    /* Expanded - stream is maximized to fill the entire stage */
    Expanded = 'Expanded',

    /* Pip - stream is minimized to occupy a small pip */
    Pip = 'Pip',

    /* None - stream currently has no significant status to report */
    None = 'None',
}

/**
 * indicate call mute state (not to be confused with `member` mute state)
 */
export enum MuteState {
    /**
     * 🔴
     *
     * Participant is muted by the server. They cannot toggle their mute state
     */
    Disabled = 'Disabled',
    /**
     * 🟡
     *
     * Participant is muted and can toggle mute state
     */
    Muted = 'Muted',
    /**
     * 🟢
     *
     * Participant can be muted and can toggle mute state
     */
    Unmuted = 'Unmuted',
}

export type ChatActivityType =
    | 'joined'
    | 'left'
    | 'transferred'
    | 'removed'
    | 'empty-chat';

export type ChatActivityMessageParticipants = {
    userId?: ParticipantID | '';
    isMe?: boolean;
    displayName: string;
    imageUrl?: string;
    timestamp?: string;
};

export type RemovedParticipant = ChatActivityMessageParticipants;

export type ChatMessageID = string | undefined;

export interface ChatMessage {
    id?: ChatMessageID;
    at?: Date;
    userId?: ParticipantID;
    displayName: string;
    message: string;
    timestamp?: string;
    topic?: string;
    isMe?: boolean;
    type: 'user-message' | 'activity';
    activityType?: ChatActivityType;
    groupedParticipants?: ChatActivityMessageParticipants[];
    removedParticipant?: RemovedParticipant;
    ref?: string; // From ChatEvent
    pending?: boolean; // Or should we store this in type?
}

export type DirectChatMessages = Map<ParticipantID, ChatMessage[]>;

export type UnseenUnreadDirectChatMessages = ChatMessageID[];

export enum ChatType {
    Direct = 'Direct',
    Everyone = 'Everyone',
}

export interface MeetingParticipant extends Participant {
    topic?: string;
}

export type CallDisplayName = {
    name: string;
    key: string;
    count?: number;
    isMe?: boolean;
};

export type ParticipantCall = {
    audio: boolean;
    callId: string;
    callDisplayName?: CallDisplayName;
    identity: string;
    identitySession: string;
    presentation: boolean;
    isSpeaking?: boolean;
    isPresenting: boolean;
    video: boolean;
};

export interface InMeetingParticipant extends MeetingParticipant {
    canChangeLayout: boolean;
    calls?: ParticipantCall[];
    imageUrl?: string;
    isClientMuted: boolean;
    isMuted: boolean;
    isCameraMuted: boolean;
    kick?: () => void;
    mute?: () => void;
    muteVideo?: () => void;
    admit?: () => void;
    spotlight?: () => void;
    setRole?: () => void;
    lowerHand?: () => void;
    supportsDirectChat: boolean;
    highlightedCharacters?: {start: number; end: number}[];
    roomId?: RoomID;
}

export enum StreamQuality {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
    VeryHigh = 'very-high',
    Auto = 'auto',
}

export enum VideoLayoutOptionValues {
    AdaptiveComposition = 'ac',
    Speaker7 = '1:7',
    Speaker21 = '1:21',
    Speaker221 = '2:21',
    Speaker33 = '1:33',
    Highlight = '1:0',
    Equal22 = '2x2',
    Equal33 = '3x3',
    Equal44 = '4x4',
    Equal55 = '5x5',
}

export interface PreviewMediaInput {
    error: DeviceError;
    preview: MediaDeviceInfoLike | undefined;
    applyChanges: () => void;
    setPreview: (device: MediaDeviceInfoLike) => void;
}

export enum NetworkState {
    Connected = 'connected',
    Reconnecting = 'reconnecting',
    Reconnected = 'reconnected',
}

export interface NormalizedStats {
    inbound: {
        audio?: InboundAudioMetrics;
        video?: InboundVideoMetrics;
        preso?: InboundVideoMetrics;
    };
    outbound: {
        audio?: OutboundAudioMetrics;
        video?: OutboundVideoMetrics;
        preso?: OutboundVideoMetrics;
    };
}

export const selfviewAspectRatioValues = [
    'camera-step',
    'in-meeting',
    'ready-to-join-express',
    'ready-to-join-step',
    'force-landscape',
] as const;
export type SelfviewAspectRatios = (typeof selfviewAspectRatioValues)[number];

export enum BreakoutRoomAssignmentMode {
    Automatically = 'Automatically',
    Manually = 'Manually',
}

export enum BreakoutRoomsScreen {
    ModeAssignment = 0,
    InitialConfiguration = 1,
    RoomsOpened = 2,
    EditConfiguration = 3,
}

export enum BreakoutRoomVariant {
    Setup = 'setup',
    Preview = 'preview',
    Edit = 'edit',
}

export type BreakoutRoomName = string;
export type BreakoutRoomId = RoomID;
export type BreakoutParticipants = Map<RoomID, InMeetingParticipant[]>;
export type BreakoutParticipantsIdentities = Map<
    BreakoutRoomId,
    ParticipantID[]
>;
export type BreakoutRoomNames = Map<BreakoutRoomId, BreakoutRoomName>;

export type BreakoutsEditPanelSave = {
    move: Map<BreakoutRoomId, BreakoutParticipantsIdentities>; // {oldRoomId: {newRoom1Id: [], newRoom2Id: []}}
    addRoom: Map<BreakoutRoomName, BreakoutParticipantsIdentities>;
    renameRoom: Map<BreakoutRoomId, BreakoutRoomName>;
    removeRoom: BreakoutRoomId[];
};

export interface BreakoutAskingForHelpStatus {
    roomId: RoomID;
    breakoutName: string;
    askForHelpTime: number;
}

export type EpochTimeStampSeconds = number;

export type ParticipantList = InMeetingParticipant[];
export type RoomTuple = [RoomID, ParticipantList];

export type Unsubscribe = () => void;
export type OnChange = () => void;
export type Subscribe = (onChange: OnChange) => Unsubscribe;
